import { useState } from 'react';
import {
  Box, Card, CardContent, Typography, IconButton, Stack, Chip,
  useMediaQuery, useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

interface BillingCode {
  type: string;
  code: string;
  description: string;
  rationale: string;
}

interface Props {
  codes: BillingCode[];
  onAccept: (accepted: BillingCode[]) => void;
}

export default function BillingSuggestions({ codes, onAccept }: Props) {
  const [accepted, setAccepted] = useState<Set<number>>(new Set(codes.map((_, i) => i)));
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const toggle = (index: number) => {
    const next = new Set(accepted);
    if (next.has(index)) next.delete(index);
    else next.add(index);
    setAccepted(next);
    onAccept(codes.filter((_, i) => next.has(i)));
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} mb={1}>Billing Suggestions</Typography>
      <Stack spacing={1}>
        {codes.map((code, i) => (
          <Card
            key={i}
            sx={{
              opacity: accepted.has(i) ? 1 : 0.5,
              border: accepted.has(i) ? '2px solid' : '2px solid transparent',
              borderColor: accepted.has(i) ? (code.type === 'CPT' ? theme.palette.accent.green : theme.palette.accent.coral) : 'transparent',
              cursor: 'pointer',
            }}
            onClick={() => toggle(i)}
          >
            <CardContent sx={{ py: 1.5, px: { xs: 1.5, sm: 2 }, '&:last-child': { pb: 1.5 } }}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    <Chip label={code.type} size="small"
                      sx={{ bgcolor: code.type === 'CPT' ? theme.palette.accent.green : theme.palette.accent.coral, color: theme.palette.text.primary, fontWeight: 700 }} />
                    <Typography variant="subtitle2" noWrap>{code.code}</Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{code.description}</Typography>
                  {!isMobile && <Typography variant="caption" color="text.secondary">{code.rationale}</Typography>}
                </Box>
                <IconButton size="small" color={accepted.has(i) ? 'success' : 'default'} onClick={(e) => { e.stopPropagation(); toggle(i); }}>
                  {accepted.has(i) ? <CheckCircleIcon /> : <CancelIcon />}
                </IconButton>
              </Stack>
              {isMobile && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{code.rationale}</Typography>}
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
}
